import { useTeam } from 'app/frontend/base/config/context/team'
import {
  useApplicationDetails,
  usePageInfo,
} from 'app/frontend/base/config/queries'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { useMemo } from 'react'
import { CellContext, ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/ui/data-table'
import {
  itemLevel,
  mythicRating,
  raidProgress,
  name,
  level,
  characterClass,
  getRaidProgressParse,
} from 'app/frontend/base/config/column-definitions/character'
import dayjs from 'dayjs'
import {
  applicationStatusColors,
  applicationStatusLabels,
} from 'app/frontend/types/custom/ApplicationStatus'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import { imageUrl } from 'app/frontend/base/helpers'
import RelativeTime from 'dayjs/plugin/relativeTime'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { buttonVariants } from '@/components/ui/button'
import ApplicationInfoBlock from './ApplicationInfoBlock'
import RefreshCharacterIcon from './RefreshCharacterIcon'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import { canCharacterHeal } from 'app/frontend/base/services/characterValues'
import ApplicationStatusActions from './ApplicationStatusActions'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import { useAuth } from 'app/frontend/base/config/context/auth'
import ApplicationMessagesDialog from './ApplicationMessagesDialog'
import ApplicationCharacterCard from 'app/frontend/base/components/ApplicationCharacterCard'
import CharacterExternalIconLink from 'app/frontend/base/components/CharacterExternalIconLink'
import { cn } from '@/lib/utils'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useTranslation } from 'react-i18next'
import ApplicationAnswers from './ApplicationAnswers'
import LinkButton from '@/components/ui/link-button'
import { useCharacterDetailsStore } from 'app/frontend/base/state/useCharacterDetailsStore'
dayjs.extend(RelativeTime)

const TeamApplicationDetails = () => {
  const { t } = useTranslation()
  const { guild, team } = useTeam()
  const { defaultDifficulty } = useDifficulties()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { accessLevel } = useAuth()
  const { applicationId } = useParams()
  const { state } = useLocation()
  const {
    data: applicationDetails,
    refetch,
    isError,
    isLoading,
  } = useApplicationDetails(
    team.id,
    applicationId ? parseInt(applicationId) : undefined,
  )

  const { detailsStore, immediatelyFetchNewDetails, fetchNewDetails } =
    useCharacterDetailsStore<wowaudit.CharacterReference>(
      (character) => character.id,
    )

  const altColumns: ColumnDef<wowaudit.CharacterReference>[] = useMemo(
    () => [
      name,
      characterClass,
      level,
      itemLevel<wowaudit.CharacterReference>(
        detailsStore,
        (character) => character.id,
      ),
      raidProgress<wowaudit.CharacterReference>(
        detailsStore,
        (character) => character.id,
        defaultDifficulty,
      ),
      {
        accessorFn: (row: wowaudit.CharacterReference) => {
          const details = detailsStore[row.id.toString() ?? '']
          return details?.mythicRating || 0
        },
        id: 'raidParse',
        size: 70,
        ...genericSorting<wowaudit.CharacterReference>(
          'alphanumeric',
          'Parse',
          false,
        ),
        cell: (info: CellContext<wowaudit.CharacterReference, unknown>) => {
          const details = detailsStore[info.row.original.id.toString() ?? '']
          if (!details) return '-'

          return (
            <div
              className={`transition-opacity duration-300 w-8 ${
                details ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <Link
                to={`${info.row.original.wclUrl}?metric=${
                  applicationDetails?.role === 'heal' &&
                  canCharacterHeal(info.row.original.classId)
                    ? 'hps'
                    : 'dps'
                }`}
                target='_blank'
                rel='noreferrer'
              >
                {
                  getRaidProgressParse(
                    details.currentRaidProgression,
                    defaultDifficulty,
                  ).element
                }
              </Link>
            </div>
          )
        },
      },
      ...(gameVersion === 'live'
        ? [
            mythicRating<wowaudit.CharacterReference>(
              detailsStore,
              (character) => character.id,
            ),
          ]
        : []),
      {
        size: gameVersion === 'live' ? 100 : 80,
        header: '',
        accessorKey: 'actions',
        cell: (info) => (
          <div className='flex items-center gap-1.5'>
            <CharacterExternalIconLink
              type='blizzard'
              backendUrl={info.row.original.blizzardUrl}
            />
            <CharacterExternalIconLink
              type='wcl'
              className='w-5 min-w-5'
              backendUrl={info.row.original.wclUrl}
            />
            {gameVersion === 'live' && (
              <CharacterExternalIconLink
                type='raiderio'
                backendUrl={info.row.original.raiderioUrl}
              />
            )}
            <RefreshCharacterIcon
              character={info.row.original}
              callback={() =>
                immediatelyFetchNewDetails([info.row.original], true)
              }
              team={team}
            />
          </div>
        ),
      },
    ],
    [detailsStore, defaultDifficulty, gameVersion],
  )

  const backToOverviewButton = (
    <LinkButton
      to={`${team.path}/applicants?${
        state?.tableParams
          ? new URLSearchParams(state.tableParams).toString()
          : ''
      }`}
      size='custom'
      className={'h-7 px-3'}
    >
      Back
    </LinkButton>
  )

  if (!applicationId || Number.isNaN(parseInt(applicationId))) {
    return <Navigate to={`${team.path}/applicants`} replace />
  }

  if (!isLoading && (isError || !applicationDetails)) {
    return <Navigate to={`${team.path}/applicants`} replace />
  }

  if (!applicationDetails) return null

  const character = applicationDetails.characterReference

  return (
    <div className='space-y-3'>
      <h2 className='flex justify-between items-center flex-wrap gap-2'>
        <span className='flex items-center gap-4'>
          <span>Application details</span> {backToOverviewButton}
        </span>

        <div className='flex items-center gap-2'>
          <ApplicationMessagesDialog type='notes' team={team} />
          <ApplicationMessagesDialog type='messages' team={team} />
        </div>
      </h2>

      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3'>
        <ApplicationInfoBlock title='Submitted'>
          <Tooltip>
            <TooltipTrigger className='text-left'>
              {dayjs(applicationDetails.createdAt).fromNow()}
            </TooltipTrigger>
            <TooltipContent>
              {dayjs(applicationDetails.createdAt).format('D MMM YYYY, HH:mm')}
            </TooltipContent>
          </Tooltip>
        </ApplicationInfoBlock>

        <ApplicationInfoBlock
          title='Status'
          className={applicationStatusColors[applicationDetails.status].text}
        >
          {accessLevel === 'admin' &&
          applicationDetails.status !== 'withdrawn' ? (
            <ApplicationStatusActions inline={true} />
          ) : (
            <span>{applicationStatusLabels[applicationDetails.status]}</span>
          )}
        </ApplicationInfoBlock>

        <ApplicationInfoBlock
          title='Battletag'
          copyValue={applicationDetails.battletag}
          className='flex items-center gap-2'
        >
          <img src={imageUrl('bnet-logo-blue.svg')} className='w-5 shrink-0' />
          <span className='truncate'>
            {applicationDetails.battletag || '-'}
          </span>
        </ApplicationInfoBlock>

        <ApplicationInfoBlock
          title='Discord ID'
          copyValue={applicationDetails.discordId}
          className='flex items-center gap-2'
        >
          <img
            src={imageUrl('discord-logo-blue.svg')}
            className='w-5 shrink-0'
          />
          <span className='truncate'>
            {applicationDetails.discordId || '-'}
          </span>
        </ApplicationInfoBlock>

        <ApplicationInfoBlock title='Age'>
          {applicationDetails.age || '-'}
        </ApplicationInfoBlock>

        <ApplicationInfoBlock title='Country'>
          {applicationDetails.country || '-'}
        </ApplicationInfoBlock>
      </div>

      {applicationDetails.previousApplication && (
        <Alert>
          <AlertTitle>Returning applicant</AlertTitle>
          <AlertDescription className='flex justify-between items-center gap-2'>
            <span>
              This player previously applied to this team and was{' '}
              <strong
                className={
                  applicationStatusColors[
                    applicationDetails.previousApplication.status
                  ].text ?? ''
                }
              >
                {applicationStatusLabels[
                  applicationDetails.previousApplication.status
                ].toLowerCase()}
              </strong>{' '}
              (
              {dayjs(
                applicationDetails.previousApplication.updated_at,
              ).fromNow()}
              ).
            </span>
            <Link
              to={`${team.path}/applicants/${applicationDetails.previousApplication.id}`}
              className={cn(buttonVariants({ variant: 'outline', size: 'xs' }))}
            >
              {t('shared.view')}
            </Link>
          </AlertDescription>
        </Alert>
      )}

      <ApplicationCharacterCard
        team={team}
        guild={guild}
        character={character}
        characterDetails={applicationDetails.characterDetails}
        role={applicationDetails.role}
        legacyCharacterPath={applicationDetails.legacyCharacterPath}
        legacyCharacterName={applicationDetails.legacyCharacterName}
        legacyCharacterClass={applicationDetails.legacyCharacterClass}
        legacyCharacterRealm={applicationDetails.legacyCharacterRealm}
        onRefresh={refetch}
      />

      <ApplicationAnswers answers={applicationDetails.answers} />

      {applicationDetails.alts && applicationDetails.alts.length > 0 && (
        <div>
          <h3 className='text-lg font-semibold mb-3 mt-6'>Alts</h3>
          <DataTable
            className='whitespace-nowrap'
            data={applicationDetails.alts}
            columns={altColumns}
            includeHeader={true}
            pagination={5}
            sortingState={[{ id: 'level', desc: true }]}
            onVisibleRowsChange={fetchNewDetails}
            getRowId={(character) => character.id.toString()}
            rowClassName={(character) =>
              character.gdprStatus === 'exists' ? '' : 'opacity-30'
            }
          />
        </div>
      )}

      <ProtectedContent
        section='applications'
        requirement='admin'
        renderUnauthorized={false}
      >
        <ApplicationStatusActions inline={false} />
      </ProtectedContent>
    </div>
  )
}

export default TeamApplicationDetails
